import { ColorPicker } from '@components/ArtBoard/ColorLibrary/ColorPicker'
import type { ColorItem, ID } from '@octopus-do/octopus-project-core'
import {
  ColorSwatch,
  TinyDropdownMenu,
  Toolbar,
} from '@octopus-do/octopus-storybook'
import { Plus } from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef } from 'react'

import { COLOR_WHITE, COLOR_WHITE_SHORT } from '@/constants/constants'
import { createSwitch } from '@/utils'
import {
  ADD_COLOR_ID,
  INHERIT_ID,
  MANAGE_ID,
  PICKER_OFFSET_Y,
  PLACEHOLDER_COUNT,
  PLACEHOLDER_ID,
  PROJECT_COLOR_MENU_ID,
} from './constants'
import { useProjectColorMenu } from './hooks'
import css from './styles.module.css'

const isWhite = (hex: string) =>
  hex.toLowerCase() === COLOR_WHITE || hex.toLowerCase() === COLOR_WHITE_SHORT

// A colour that matches the plate gets a ring so it stays visible.
const swatchVariant = (hex: string) => (isWhite(hex) ? 'white' : 'filled')

const PLACEHOLDER_KEYS = Array.from(
  { length: PLACEHOLDER_COUNT },
  (_, index) => `${PLACEHOLDER_ID}-${index}`
)

interface ContentProps {
  hideManage?: boolean
  colors: ColorItem[]
  inheritLabel: string
  locked: boolean
}

// Without the colour library: no add and no manage, a PRO divider and grey slots
// under the colours instead (Figma 86153:859440). Also the color menu of a tag.
export const ProjectColorMenuContent: React.FC<ContentProps> = ({
  colors,
  inheritLabel,
  locked,
  hideManage = false,
}) => (
  <>
    {inheritLabel && (
      <>
        <TinyDropdownMenu.SelectOption id={INHERIT_ID} label={inheritLabel} />
        <TinyDropdownMenu.Divider />
      </>
    )}
    <TinyDropdownMenu.Grid>
      {colors.map(({ hexCode, id, title }) => (
        <TinyDropdownMenu.IconOption id={id} key={id} tooltip={title}>
          <ColorSwatch color={hexCode} variant={swatchVariant(hexCode)} />
        </TinyDropdownMenu.IconOption>
      ))}
      {!locked && (
        <TinyDropdownMenu.IconOption
          icon={Plus}
          id={ADD_COLOR_ID}
          tooltip="Add color"
        />
      )}
    </TinyDropdownMenu.Grid>
    {locked ? (
      <>
        <TinyDropdownMenu.Divider badge="pro_muted" />
        <TinyDropdownMenu.Grid>
          {PLACEHOLDER_KEYS.map((key) => (
            <TinyDropdownMenu.IconOption id={PLACEHOLDER_ID} key={key} locked>
              <ColorSwatch color="" variant="disabled" />
            </TinyDropdownMenu.IconOption>
          ))}
        </TinyDropdownMenu.Grid>
      </>
    ) : (
      !hideManage && <TinyDropdownMenu.Footer id={MANAGE_ID} label="Manage" />
    )}
  </>
)

interface TriggerProps {
  color: string
  disabled: boolean
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ color, disabled }, ref) => {
    const { active, toggle } = TinyDropdownMenu.useTrigger()

    return (
      <Toolbar.ColorButton
        active={active}
        color={color}
        disabled={disabled}
        onClick={toggle}
        ref={ref}
        tooltip="Color"
        variant={swatchVariant(color)}
      />
    )
  }
)

interface Props {
  colorId: ID
  disabled?: boolean
  // A colour of its own is optional for this entity: the menu gets a row above
  // the palette with this label, and picking it reports an empty colour id.
  // `inheritHex` is what the entity is painted with meanwhile — the trigger
  // shows it, so the button never lies about the colour on the artboard.
  inheritHex?: string
  inheritLabel?: string
  // Runs after the colour library opens from Manage, e.g. to drop a selection.
  onManage?: () => void
}

// Reports the picked colour id to Toolbar.Root under PROJECT_COLOR_MENU_ID.
export const ProjectColorMenu: React.FC<Props> = ({
  colorId,
  disabled = false,
  inheritHex = '',
  inheritLabel = '',
  onManage = () => {},
}) => {
  const { onClick: pick } = Toolbar.useToolbarClick(PROJECT_COLOR_MENU_ID)
  const { closeMenus } = Toolbar.useToolbarContext()

  const onPick = (id: ID) => pick([id])

  const {
    colorHex,
    colorHexUI,
    colors,
    locked,
    onAdd,
    onManageClick,
    onUpgrade,
    picker,
  } = useProjectColorMenu(colorId, onPick, onManage)

  // The picker is not a storybook menu: it closes the toolbar's own menus and
  // popovers before it opens, one open dropdown per toolbar.
  const onAddColor = () => {
    closeMenus()
    onAdd()
  }

  const inherited = Boolean(inheritLabel) && !colorId

  const switcher = createSwitch()
    .case([ADD_COLOR_ID], onAddColor)
    .case([INHERIT_ID], () => onPick(''))
    .case([MANAGE_ID], onManageClick)
    .case([PLACEHOLDER_ID], onUpgrade)
    .case([], onPick)

  const onClick = (keys: string[]) => switcher.run(keys)

  return (
    <div className={css.container} ref={picker.setArea}>
      <TinyDropdownMenu.Root
        active={[inherited ? INHERIT_ID : colorId]}
        colors={colors}
        content={ProjectColorMenuContent}
        inheritLabel={inheritLabel}
        locked={locked}
        onClick={onClick}
        width="fit"
      >
        <Trigger
          color={inherited ? inheritHex : colorHexUI}
          disabled={disabled}
        />
      </TinyDropdownMenu.Root>

      {picker.shown && (
        <ColorPicker
          colorHex={colorHex}
          colorPikerAreaRef={picker.area}
          offset={{ y: PICKER_OFFSET_Y }}
          onColorChange={picker.onPreview}
          onOutsideClick={picker.onClose}
          onSetColor={picker.onAddColor}
          position="bottom"
        />
      )}
    </div>
  )
}
